import React, { Component } from "react";
import { connect } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { getLineStaLabel, getLocationName } from "./utils/adrUtilsV3";
import "./styles/adrTentativeAgreement.css";
import adrActions from "../../_actions/adrActions";
import { ADR_EDIT } from "../../_constants/actionConstants";
import AdrTentativeAgreementChangeJustificationModalV3 from "./AdrTentativeAgreementChangeJustificationModalV3";
import AdrTentativeAgreementModalTabsV3 from "./AdrTentativeAgreementModalTabsV3";
import AdrTentativeAgreementFileUploadV3 from "./AdrTentativeAgreementFileUploadV3";
import { SUBLOCATION_WORKPERFORMED_TYPE } from "./constants/adrConstantsV3";
import cem4907PdfActions from "../../_actions/cem4907PdfActions";
import moment from "moment/moment";
import AlertModal from "../_common/AlertModal";
import memoizeOne from "memoize-one";
import AdrTentativeAgreementSignaturesV3 from "./AdrTentativeAgreementSignaturesV3";
import AlertBox from "../_common/AlertBox";
import { actionScripts } from "../../_helpers/actionScripts";
import { AdrTentativeAgreementModal_LaborSetV3 } from "./AdrTentativeAgreementModal_LaborSetV3";
import { AdrTentativeAgreementModal_EquipmentSetV3 } from "./AdrTentativeAgreementModal_EquipmentSetV3";
import { AdrTentativeAgreementModal_MaterialsV3 } from "./AdrTentativeAgreementModal_MaterialsV3";
import AdrTentativeAgreementCustomFormUploadV3 from "./AdrTentativeAgreementCustomFormUploadV3";
import AdrTentativeAgreementSendEmailModalV3 from "./AdrTentativeAgreementSendEmailModalV3";

/**
 * Tentative Agreement front-end business rules:
 * - TA is saved upon 'Apply & Close'.
 *   - But the TA's Labor/Equipment data are subject to change from
 *     the base daily report's labor and equipment data.
 * - Once TA is signed, it is locked from further editing (isLocked set to true).
 * - Until TA is signed, all LaborSetRows from the base daily report are imported
 *   for editing.
 */
class AdrTentativeAgreementModalV3 extends Component {
  state = {
    // Kept null when TA is new, otherwise overwritten by incoming (always a nonzero int)
    clientId: null,
    sequence: 0,
    tentativeAgreementId: 0,
    wpTabId: 0,
    isLocked: false,
    /** @type {number[]} */
    selectedContractors: [],
    /** @type {Object.<number, {locationId: number, sublocationWorkPerformedId: number?}[]} */
    selectedLocations: {}, // Key is locWp.clientId
    workPerformedId: 0,
    workDescription: "",
    workDescriptionDraftState: "",
    applyHoursToDR: false,
    /** @type {string} */
    selectedTabId: "labor",
    /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementLaborClient} */
    laborsClient: null,
    /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementEquipmentClient} */
    equipmentClient: null,
    /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialView[]>} */
    materialsView: null,
    /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementAttachmentDto[]} */
    attachments: [], // Derived from props (redux store), managed as state
    /** @type {string[]} */
    caltransRepSignature: "", // dataURL, image/png
    isWetSignedByCaltransRep: false,
    caltransRepSignedOn: null,
    caltransRepRemarks: "",
    caltransRepName: "",
    isDisputedByCaltransRep: false,
    /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementContractorDto>} */
    contractorsClient: {},
    pdfFilePath: "",
    isCustomTa: false,
    selectedFormType: "cem4907",
    showModal_email: false,
    showAlert_changeFormTypeConfirm: false,
    showAlert_deleteConfirm: false,
    showAlert_lockConfirm: false,
    showAlert_cancelConfirm: false,
    isNew: false, // Updated from props.isNew at first, but can be updated by setIsNew
    isChanged: false, // Updated by markTaDirty/markTaClean
  };

  tabs = [
    { tabId: "labor", link: "#", label: "Labor" },
    { tabId: "equipment", link: "#", label: "Equipment" },
    { tabId: "materials", link: "#", label: "Materials" },
  ];

  _selectedFormType;

  componentDidMount() {
    const { clientId, adr, isNew } = this.props;

    // Derive the state from props if TA is loaded from existing. (If new, just stick with
    // the default state.)

    /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} */
    const ta = adr.tentativeAgreements.find((ta) => ta.clientId === clientId) || null;
    const selectedContractors =
      ta && ta.contractorsClient
        ? Object.keys(ta.contractorsClient).map((cId) => +cId)
        : [];
    const selectedLocations = this.getSelectedLocations(ta);

    const taState = {
      ...this.state,
      // Copy contractorsClient only if this is an existing TA being edited.
      // contractorsClient will be a blank obj if the TA is new.
      ...(ta
        ? {
            ...ta,
            selectedFormType: ta.isCustomTa ? "custom" : "cem4907",
            selectedLocations,
            contractorsClient: _.cloneDeep(ta.contractorsClient || {}),
            attachments: _.cloneDeep(ta.attachments || []),
          }
        : {}),
      laborsClient: this.initLaborsClient(selectedContractors, ta),
      equipmentClient: this.initEquipmentClient(selectedContractors, ta),
      materialsView: this.initMaterialsView(selectedLocations, ta),
      isNew,
    };

    this._selectedFormType = taState.selectedFormType;
    this.setState({ ...taState });
  }

  /**
   *
   * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} ta
   * @returns {Object.<number, {locationId: number, sublocationWorkPerformedId: number?}[]} - key is locWp.clientId
   */
  getSelectedLocations = (ta) => {
    const { workPerformedClient: wpc } = this.props;

    if (!ta || !ta.wpTabId) return {};

    // Collect locWps with references to its MM from existing TA's materialsClient, or those
    // that were selected but without material refs
    /** @type {Object.<number, {locationId: number, sublocationWorkPerformedId: number?}[]} */
    let selectedLocations = {};

    (wpc[ta.wpTabId].locationWorkPerformed || []).forEach((locWp) => {
      const locId = locWp.isSublocation ? locWp.sublocationId : locWp.locationId;
      const matchFoundFromMms = locWp.miscMaterials.some(
        (locMm) =>
          (!locWp.isSublocation &&
            locId in ta.materialsClient &&
            ta.materialsClient[locId].some(
              (tamc) => tamc.locationMiscMaterialsId === locMm.locationMiscMaterialsId
            )) ||
          (locWp.isSublocation &&
            locId in ta.materialsClient &&
            ta.materialsClient[locId].some(
              (tamc) =>
                tamc.sublocationMiscMaterialsId === locMm.sublocationMiscMaterialsId
            ))
      );
      const matchFoundFromLocs = locWp.isSublocation
        ? Object.values(ta.locations).find(
            (tal) =>
              tal.locationId === locWp.sublocationId &&
              tal.sublocationWorkPerformedId === locWp.sublocationWorkPerformedId
          )
        : Object.values(ta.locations).find((tal) => tal.locationId === locWp.locationId);
      if (matchFoundFromMms || matchFoundFromLocs)
        selectedLocations[locWp.clientId] = {
          locationId: locWp.isSublocation ? locWp.sublocationId : locWp.locationId,
          sublocationWorkPerformedId: locWp.isSublocation
            ? locWp.sublocationWorkPerformedId
            : null,
        };
    });

    return selectedLocations;
  };

  /**
   * Initialize laborsClient by mapping each adr.laborSetClient to ta.laborsClient
   * @param {number[]} selectedContractors
   */
  initLaborsClient = (selectedContractors, existTa) => {
    const { adr, isNew } = this.props;
    /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementLaborClient>} */
    let laborsClient = {};
    for (const lsrKey in adr.laborSetClient) {
      // Check for an existing TentativeAgreementLabor and get it, if not, set to null.
      const lsr = adr.laborSetClient[lsrKey];
      const taLaborKey = `${lsr.laborId}.${lsr.tradeId}`;
      let tal =
        (existTa && existTa.laborsClient && existTa.laborsClient[taLaborKey]) || null;
      if (tal && !selectedContractors.includes(tal.contractorId)) {
        delete existTa.laborsClient[taLaborKey];
        tal = null;
      }
      // If the lsr is incomplete, skip
      if (
        !adr.laborSetClient[lsrKey].contractorId ||
        !adr.laborSetClient[lsrKey].laborId ||
        !adr.laborSetClient[lsrKey].tradeId ||
        !selectedContractors.includes(adr.laborSetClient[lsrKey].contractorId)
      )
        continue;

      laborsClient[taLaborKey] = {
        rowKey: taLaborKey,
        excludeFromTa: tal ? tal.excludeFromTa : isNew ? false : true, // Default is exclude, if TAL doesn't exist
        tentativeAgreementLaborId: tal ? tal.tentativeAgreementLaborId : 0,
        tentativeAgreementId: isNew ? existTa.tentativeAgreementId : 0,
        contractorId: tal ? tal.contractorId : adr.laborSetClient[lsrKey].contractorId,
        laborId: tal ? tal.laborId : adr.laborSetClient[lsrKey].laborId,
        tradeId: tal ? tal.tradeId : adr.laborSetClient[lsrKey].tradeId,
        regHours: tal
          ? tal.regHours
          : adr.laborSetClient[lsrKey].laborHours[existTa.wpTabId].regHours,
        otHours: tal
          ? tal.otHours
          : adr.laborSetClient[lsrKey].laborHours[existTa.wpTabId].otHours,
        changeJustification: tal ? tal.changeJustification : "",
      };
    }
    return laborsClient;
  };

  /**
   * Populate equipmentClient same way as laborsClient above.
   * @param {number[]} selectedContractors
   */
  initEquipmentClient = (selectedContractors, existTa) => {
    const { adr, isNew } = this.props;
    /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementEquipmentClient>} */
    let equipmentClient = {};
    for (const esrKey in adr.equipmentSetClient) {
      const equipmentId = adr.equipmentSetClient[esrKey].equipmentId;
      // Check for an existing TentativeAgreementEquipment and get it, if not, set to null.
      let tae =
        (existTa && existTa.equipmentClient && existTa.equipmentClient[equipmentId]) ||
        null;
      if (tae && !selectedContractors.includes(tae.contractorId)) {
        delete existTa.equipmentClient[equipmentId];
        tae = null;
      }

      if (
        !adr.equipmentSetClient[esrKey].contractorId ||
        !adr.equipmentSetClient[esrKey].equipmentId ||
        !selectedContractors.includes(adr.equipmentSetClient[esrKey].contractorId)
      )
        continue;

      equipmentClient[equipmentId] = {
        rowKey: equipmentId,
        excludeFromTa: tae ? tae.excludeFromTa : isNew ? false : true, // Default is exclude if TAE doesn't exist
        tentativeAgreementEquipmentId: tae ? tae.tentativeAgreementEquipmentId : 0,
        tentativeAgreementId: existTa ? existTa.tentativeAgreementId : 0,
        contractorId: tae
          ? tae.contractorId
          : adr.equipmentSetClient[esrKey].contractorId,
        equipmentId: tae ? tae.equipmentId : adr.equipmentSetClient[esrKey].equipmentId,
        regHours: tae
          ? tae.regHours
          : adr.equipmentSetClient[esrKey].equipmentHours[existTa.wpTabId].regHours,
        otHours: tae
          ? tae.otHours
          : adr.equipmentSetClient[esrKey].equipmentHours[existTa.wpTabId].otHours,
        changeJustification: tae ? tae.changeJustification : "",
      };
    }
    return equipmentClient;
  };

  /**
   * When this component is mounted, this.props.adr.tentativeAgreements.materialsClient
   * is of type {Object.<number, TentativeAgreementMaterialClient[]>}, where the key is
   * locationId. This component derives this into {TentativeAgreementMaterialView}, which
   * has a superset of properties of {TentativeAgreementMaterialClient} to allow for
   * controlling "excludeFromTa" checkbox and for rendering location names.
   * This function maps all incoming clients into the view shape at the time of mounting
   * to facilitate the initialization of component state.
   * Called only once at CDM.
   * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} ta
   */
  mapMaterialsClientToView = (ta) => {
    const { workPerformedClient: wpc } = this.props;

    /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialView[]>} */
    let taMaterialsView = {};

    for (const locId in ta.materialsClient) {
      ta.materialsClient[locId].forEach((tamc, index) => {
        /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient} */
        const locWp = (wpc[ta.wpTabId].locationWorkPerformed || []).find((locWp) =>
          locWp.miscMaterials.some((locMm) =>
            locWp.isSublocation
              ? locMm.sublocationMiscMaterialsId === tamc.sublocationMiscMaterialsId
              : locMm.locationMiscMaterialsId === tamc.locationMiscMaterialsId
          )
        );
        const locMm = ((locWp && locWp.miscMaterials) || []).find((locMm) =>
          locWp && locWp.isSublocation
            ? locMm.sublocationMiscMaterialsId === tamc.sublocationMiscMaterialsId
            : locMm.locationMiscMaterialsId === tamc.locationMiscMaterialsId
        );
        if (locMm) {
          taMaterialsView[locId] = [
            ...(locId in taMaterialsView ? taMaterialsView[locId] : []),
            {
              ...tamc,
              key: `mmId_${
                locWp.isSublocation
                  ? tamc.sublocationMiscMaterialsId
                  : tamc.locationMiscMaterialsId
              }-tam_${index}`,
              // locWpClientId is needed to cross reference selectedLocations
              locWpClientId: locWp.clientId,
              isSublocation: locWp.isSublocation,
              locationName: this.getLocationNameForTa(locWp),
              description: locMm.materialDescription,
              vendorName: locMm.vendorName,
              invoiceNo: locMm.invoiceNo,
              quantity: locMm.quantity,
              miscMaterialsUnitOfMeasureId: locMm.miscMaterialsUnitOfMeasureId,
              stationFromOrAt: locWp.stationFromOrAt,
              stationFromOrAtOffsetValue: locWp.stationFromOrAtOffsetValue,
              stationFromOrAtOffsetDirection: locWp.stationFromOrAtOffsetDirection,
              stationTo: locWp.stationTo,
              stationToOffsetValue: locWp.stationToOffsetValue,
              stationToOffsetDirection: locWp.stationToOffsetDirection,
            },
          ];
        }
      });
    }

    return taMaterialsView;
  };

  initMaterialsView = (selectedLocations, ta) => {
    console.log("ta=", ta);
    if (!ta || !ta.materialsClient) return {};

    const existTamViews = this.mapMaterialsClientToView(ta);
    return this.getUpdatedMaterialsClientView(
      selectedLocations,
      existTamViews,
      ta.wpTabId
    );
  };

  /**
   * Updates TamcViews when location selection changes
   * @param {Object.<number, {locationId: number, sublocationWorkPerformedId: number?}[]} selectedLocations
   * @param {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialView[]} tamViewsToUpdate
   * @param {number} wpTabId - usually available thru the state, but state.wpTabId is NOT available when initializing.
   * @returns {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialView[]}
   */
  getUpdatedMaterialsClientView = (selectedLocations, tamViewsToUpdate, wpTabId) => {
    const { workPerformedClient: wpc } = this.props;
    const { isLocked, tentativeAgreementId } = this.state;

    // If TA is locked, do not perform any updates.
    if (isLocked) return;

    // Get a list of all locMms for the selected locations within current wpc
    /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient[]} */
    const selectedLocWps = (wpc[wpTabId].locationWorkPerformed || []).filter(
      (locWp) => locWp.clientId in selectedLocations && locWp.miscMaterials
    );

    /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialView[]} */
    let updatedTamViews = {};
    selectedLocWps.forEach((locWp) => {
      locWp.miscMaterials.forEach(
        /**
         * @param {import("../../_typedefs/adrV3TypeDefs").LocationMiscMaterialsDto|import("../../_typedefs/adrV3TypeDefs").SublocationMiscMaterialsDto} locMm
         * @param {number} index
         */
        (locMm, index) => {
          const locationId = locWp.isSublocation ? locWp.sublocationId : locWp.locationId;
          // Check if a matching TaMaterials exists for each locWp's MiscMaterials.
          // MiscMaterials can either be a LocMM or special subloc's SublocMM.
          /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialView} */
          const existingTamv =
            locationId in tamViewsToUpdate &&
            tamViewsToUpdate[locationId].find((tamv) =>
              locWp.isSublocation
                ? locMm.sublocationMiscMaterialsId === tamv.sublocationMiscMaterialsId
                : locMm.locationMiscMaterialsId === tamv.locationMiscMaterialsId
            );
          updatedTamViews[locationId] = [
            // taMatList[locId] could already exist for special sublocs
            // (or any other special subloc), so if it does, append new data to it. Explained below:
            // For each locWp, we add to taMaterialsList[locId]. The locId used for key is sublocationId
            // for special sublocs (instead of -2 for line/sta, for example.) There could be multiple
            // locWps with same sublocationId - in which case we need to append instead of overwrite.
            ...(locationId in updatedTamViews ? updatedTamViews[locationId] : []),
            existingTamv
              ? { ...existingTamv }
              : {
                  clientId: 0,
                  key: `mmId_${
                    locWp.isSublocation
                      ? locMm.sublocationMiscMaterialsId
                      : locMm.locationMiscMaterialsId
                  }-tam_${index}`,
                  // locWpClientId is needed to cross reference selectedLocations
                  locWpClientId: locWp.clientId,
                  tentativeAgreementId,
                  tentativeAgreementMaterialId: 0, // new, so set as 0
                  locationId,
                  isSublocation: locWp.isSublocation,
                  locationMiscMaterialsId: locWp.isSublocation
                    ? null
                    : locMm.locationMiscMaterialsId,
                  sublocationMiscMaterialsId: locWp.isSublocation
                    ? locMm.sublocationMiscMaterialsId
                    : null,
                  locationName: this.getLocationNameForTa(locWp),
                  description: locMm.materialDescription,
                  vendorName: locMm.vendorName,
                  invoiceNo: locMm.invoiceNo,
                  quantity: locMm.quantity,
                  miscMaterialsUnitOfMeasureId: locMm.miscMaterialsUnitOfMeasureId,
                  stationFromOrAt: locWp.stationFromOrAt,
                  stationFromOrAtOffsetValue: locWp.stationFromOrAtOffsetValue,
                  stationFromOrAtOffsetDirection: locWp.stationFromOrAtOffsetDirection,
                  stationTo: locWp.stationTo,
                  stationToOffsetValue: locWp.stationToOffsetValue,
                  stationToOffsetDirection: locWp.stationToOffsetDirection,
                  excludeFromTa: true,
                  changeJustification: "",
                },
          ];
        }
      );
    });
    return updatedTamViews;
  };

  getLocationSet = () => {
    const { workPerformedClient: wpc } = this.props;
    const { wpTabId } = this.state;
    if (!wpTabId) return [];

    /** @type {{clientId: number, locationName: string}[]} */
    let locationSet = [];
    (wpc[wpTabId].locationWorkPerformed || []).forEach((locWp) => {
      locationSet.push({
        clientId: locWp.clientId,
        locationName: this.getLocationNameForTa(locWp),
      });
    });

    return locationSet;
  };

  /**
   * Utility for generating location name based on locWp's type.
   * Function is named with "ForTa" suffix to distinguish from this.props.getLocationName(),
   * which is used to get location name for non-sublocs.
   * @param {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient} locWp
   * @returns {string}
   */
  getLocationNameForTa = (locWp) => {
    const { projLocationList } = this.props;
    return locWp.isSublocation
      ? locWp.sublocationWorkPerformedType === SUBLOCATION_WORKPERFORMED_TYPE.ALIGNMENT
        ? getLineStaLabel(locWp, projLocationList)
        : ""
      : getLocationName(locWp.locationId, projLocationList);
  };

  markTaDirty = () => this.setState({ isChanged: true });
  markTaClean = () => this.setState({ isChanged: false });
  setIsNew = (isNew) => this.setState({ isNew });

  toggleLocation = (locWpClientId) => (e) => {
    e.preventDefault();
    const { workPerformedClient: wpc } = this.props;
    const { isChanged, materialsView, wpTabId } = this.state;
    let selectedLocations = _.cloneDeep(this.state.selectedLocations);

    if (locWpClientId in selectedLocations) delete selectedLocations[locWpClientId];
    else {
      const locWp = (wpc[wpTabId].locationWorkPerformed || []).find(
        (locWp) => locWp.clientId === locWpClientId
      );
      selectedLocations[locWpClientId] = {
        locationId: locWp.isSublocation ? locWp.sublocationId : locWp.locationId,
        sublocationWorkPerformedId: locWp.isSublocation
          ? locWp.sublocationWorkPerformedId
          : null,
      };
    }

    const updatedTamViews = this.getUpdatedMaterialsClientView(
      selectedLocations,
      materialsView,
      wpTabId
    );

    this.setState(
      {
        selectedLocations,
        materialsView: updatedTamViews,
      },
      () => !isChanged && this.markTaDirty()
    );
  };

  selectTab = (tabId) => this.setState({ selectedTabId: tabId });

  handleChange = (e) =>
    this.setState(
      { [e.target.name]: e.target.value },
      () => !this.state.isChanged && this.markTaDirty()
    );

  handleChange_check = (e) =>
    this.setState(
      { [e.target.name]: e.target.checked },
      () => !this.state.isChanged && this.markTaDirty()
    );

  handleChange_taType = (taType) => () => {
    // Must clear any existing signatures when changing the form type.
    // Alert user to confirm if this is the case.
    const { contractorsClient, caltransRepSignedOn, attachments } = this.state;
    this._selectedFormType = taType;
    if (
      Object.values(contractorsClient).some((c) => c.contractorRepSignedOn) ||
      caltransRepSignedOn ||
      (taType === "cem4907" &&
        (attachments || []).some((a) => !a.isDeleted && a.isCustomTa))
    )
      this.toggleAlert_changeFormTypeConfirm();
    else
      this.setState(
        { selectedFormType: taType, isCustomTa: taType !== "cem4907" },
        () => !this.state.isChanged && this.markTaDirty()
      );
  };

  updateLabor = (rowKey, fieldsToUpdate) => {
    let taLabor = { ...this.state.laborsClient[rowKey] };
    for (const property in fieldsToUpdate) {
      if (property in taLabor)
        taLabor = { ...taLabor, [property]: fieldsToUpdate[property] };
    }
    this.setState(
      {
        laborsClient: {
          ...this.state.laborsClient,
          [rowKey]: taLabor,
        },
      },
      () => !this.state.isChanged && this.markTaDirty()
    );
  };

  updateEquipment = (rowKey, fieldsToUpdate) => {
    let taEquipment = { ...this.state.equipmentClient[rowKey] };
    for (const property in fieldsToUpdate) {
      if (property in taEquipment)
        taEquipment = { ...taEquipment, [property]: fieldsToUpdate[property] };
    }
    this.setState(
      {
        equipmentClient: {
          ...this.state.equipmentClient,
          [rowKey]: taEquipment,
        },
      },
      () => !this.state.isChanged && this.markTaDirty()
    );
  };

  /**
   * Updates materialsView in component state. It does NOT dispatch an update
   * to the redux store - the store update is ONLY dispatched in this.save().
   * @param {number} locationId
   * @param {string} key - NOT the clientId. ClientId identifies TAMs in redux store only.
   * @param {Object} fieldsToUpdate
   */
  updateMaterial = (locationId, key, fieldsToUpdate) => {
    const materialsView = _.cloneDeep(this.state.materialsView);
    const tamToUpdate = materialsView[locationId].find((tam) => tam.key === key);
    if (!tamToUpdate) return;
    for (const property in fieldsToUpdate) {
      tamToUpdate[property] = fieldsToUpdate[property];
    }
    this.setState({ materialsView }, () => !this.state.isChanged && this.markTaDirty());
  };

  addAttachments = (attachmentsToAdd) =>
    this.setState(
      { attachments: [...this.state.attachments, ...attachmentsToAdd] },
      () => !this.state.isChanged && this.markTaDirty()
    );

  addCustomFormAttachment = (cfToAdd) => {
    const attachments = [...this.state.attachments];
    attachments.forEach((a) => {
      if (a.isCustomTa) a.isDeleted = true;
    });
    this.setState({ attachments: [...attachments, cfToAdd] }, () => {
      if (!this.state.isChanged) this.markTaDirty();
      this.props.addTempFilePaths([cfToAdd.filePath]);
      if (cfToAdd.imageThumbnailFilePath)
        this.props.addTempFilePaths([cfToAdd.imageThumbnailFilePath]);
    });
  };

  updateAttachment = (fileRowIndex, fieldsToUpdate) => {
    const attachments = [...this.state.attachments];
    for (const fieldToUpdate in fieldsToUpdate) {
      const attToUpdate = attachments.filter((a) => !a.isDeleted)[fileRowIndex];
      if (attToUpdate && fieldToUpdate in attToUpdate)
        attToUpdate[fieldToUpdate] = fieldsToUpdate[fieldToUpdate];
    }

    this.setState({ attachments }, () => !this.state.isChanged && this.markTaDirty());
  };

  deleteCustomFormAttachment = () => {
    const _attachments = [...this.state.attachments];
    _attachments.forEach((a) => {
      if (a.isCustomTa) a.isDeleted = true;
    });
    this.setState(
      { attachments: _attachments },
      () => !this.state.isChanged && this.markTaDirty()
    );
  };

  applyCaltransSignature = async (signData) => {
    const { isLocked: prevIsLocked, contractorsClient } = this.state;
    const { adr, saveAdr } = this.props;

    if (!signData) return;

    const ctSignedOrDisputed =
      signData.caltransRepSignedOn || signData.isDisputedByCaltransRep;
    const contractorSignedOrDisputed =
      contractorsClient &&
      Object.values(contractorsClient).some((cc) => cc.contractorRepSignedOn);
    this.setState(
      {
        ...signData,
        isLocked: ctSignedOrDisputed || contractorSignedOrDisputed ? true : false,
      },
      async () => {
        // When signing, save
        this.save();
        await saveAdr(adr.assistantDailyReportId);
        if (!prevIsLocked && this.state.isLocked) this.toggleAlert_lockConfirm();
      }
    );
  };

  applyContractorSignatures = (contractorsClient) => {
    const {
      isLocked: prevIsLocked,
      caltransRepSignedOn,
      isDisputedByCaltransRep,
    } = this.state;
    const { adr, saveAdr } = this.props;

    const contractorSignedOrDisputed =
      contractorsClient &&
      Object.values(contractorsClient).some(
        (cc) => cc.contractorRepSignedOn || cc.isDisputedByContractor
      );

    const ctSignedOrDisputed = caltransRepSignedOn || isDisputedByCaltransRep;

    this.setState(
      {
        contractorsClient,
        isLocked: contractorSignedOrDisputed || ctSignedOrDisputed ? true : false,
      },
      async () => {
        // When signing, save
        this.save();
        await saveAdr(adr.assistantDailyReportId);
        if (!prevIsLocked && this.state.isLocked) this.toggleAlert_lockConfirm();
      }
    );
  };

  applyChangeJustification_labor = (rowKey) => (cj) => {
    const laborsClient = _.cloneDeep(this.state.laborsClient);
    laborsClient[rowKey].changeJustification = cj;
    this.setState({ laborsClient }, () => this.markTaDirty());
  };

  applyChangeJustification_equipment = (rowKey) => (cj) => {
    const equipmentClient = _.cloneDeep(this.state.equipmentClient);
    equipmentClient[rowKey].changeJustification = cj;
    this.setState({ equipmentClient }, () => this.markTaDirty());
  };

  applyChangeJustification_materials = (locationId, key) => (cj) => {
    const materialsView = _.cloneDeep(this.state.materialsView);
    const tamToUpdate = materialsView[locationId].find((tam) => tam.key === key);
    if (!tamToUpdate) return;
    tamToUpdate.changeJustification = cj;
    this.setState({ materialsView }, () => this.markTaDirty());
  };

  save = () => {
    const { save, adr, clearTempFilePaths } = this.props;
    const { selectedLocations, isLocked } = this.state;

    const locations = Object.values(selectedLocations).map((sl) => ({
      locationId: sl.locationId,
      sublocationWorkPerformedId: sl.sublocationWorkPerformedId,
    }));

    // Filter out all unchecked labor/equipment/material rows - unless, if 'isLocked',
    // include rows where 'excludeFromTa' is TRUE.
    let laborsClient = {};
    for (const taLaborKey in this.state.laborsClient) {
      if (
        (isLocked && this.state.laborsClient[taLaborKey].changeJustification) ||
        !this.state.laborsClient[taLaborKey].excludeFromTa
      )
        laborsClient[taLaborKey] = { ...this.state.laborsClient[taLaborKey] };
    }

    let equipmentClient = {};
    for (const equipmentId in this.state.equipmentClient) {
      if (
        (isLocked && this.state.equipmentClient[equipmentId].changeJustification) ||
        !this.state.equipmentClient[equipmentId].excludeFromTa
      )
        equipmentClient[equipmentId] = { ...this.state.equipmentClient[equipmentId] };
    }

    /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialClient[]>} */
    let materialsClient = {};
    const getCurrMaxClientId = (locId) =>
      Math.max(
        ...Object.values(this.state.materialsView[locId])
          .filter((tam) => !isNaN(tam.clientId))
          .map((tam) => tam.clientId)
      );
    for (const locId in this.state.materialsView) {
      materialsClient[locId] = this.state.materialsView[locId]
        .filter((tamc) => (isLocked && tamc.changeJustification) || !tamc.excludeFromTa)
        .map((tamc) => ({
          clientId: tamc.clientId || getCurrMaxClientId(locId) + 1,
          tentativeAgreementMaterialId: tamc.tentativeAgreementMaterialId || 0,
          tentativeAgreementId: tamc.tentativeAgreementId || 0,
          locationId: +locId,
          locationMiscMaterialsId: tamc.locationMiscMaterialsId || null,
          sublocationMiscMaterialsId: tamc.sublocationMiscMaterialsId || null,
          excludeFromTa: tamc.excludeFromTa,
          changeJustification: tamc.changeJustification,
        }));
      if (materialsClient[locId].length === 0) delete materialsClient[locId];
    }

    const {
      clientId,
      sequence,
      tentativeAgreementId,
      workPerformedId,
      wpTabId,
      workDescription,
      workDescriptionDraftState,
      applyHoursToDR,
      caltransRepSignature,
      isWetSignedByCaltransRep,
      caltransRepSignedOn,
      caltransRepName,
      caltransRepRemarks,
      isDisputedByCaltransRep,
      contractorsClient,
      attachments,
      selectedFormType,
      isNew,
    } = this.state;
    // this.state has fields that are not needed for saving, so
    // a clean TaClient object is created here.
    /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} */
    const taToSave = {
      clientId,
      sequence,
      tentativeAgreementId, // If new, this is 0
      assistantDailyReportId: adr.assistantDailyReportId, // Save wall prevents this from being 0
      workPerformedId,
      wpTabId,
      createdOn: Date.now(),
      isLocked,
      workDescription,
      workDescriptionDraftState,
      locations,
      caltransRepSignature,
      isWetSignedByCaltransRep,
      caltransRepSignedOn,
      caltransRepName,
      caltransRepRemarks,
      isDisputedByCaltransRep,
      contractorsClient: _.cloneDeep(contractorsClient),
      laborsClient,
      equipmentClient,
      materialsClient,
      attachments: attachments.map((taa) => ({ ...taa })),
      isCustomTa: selectedFormType === "custom",
    };

    // console.log("saving=", taToSave);

    // Apply changes made to the hours
    save(taToSave, applyHoursToDR);
    clearTempFilePaths();

    this.markTaClean();
    if (isNew) this.setIsNew(false);
  };

  toggleModal_email = () =>
    this.setState({ showModal_email: !this.state.showModal_email });

  toggleAlert_changeFormTypeConfirm = () =>
    this.setState({
      showAlert_changeFormTypeConfirm: !this.state.showAlert_changeFormTypeConfirm,
    });

  toggleAlert_deleteConfirm = () =>
    this.setState({ showAlert_deleteConfirm: !this.state.showAlert_deleteConfirm });

  toggleAlert_lockConfirm = () =>
    this.setState({ showAlert_lockConfirm: !this.state.showAlert_lockConfirm });

  toggleAlert_cancelConfirm = () =>
    this.setState({ showAlert_cancelConfirm: !this.state.showAlert_cancelConfirm });

  handleAlert_changeFormTypeConfirm = (actionType) => (e) => {
    e.preventDefault();
    this.toggleAlert_changeFormTypeConfirm();

    if (actionType === "accept") {
      const attachments = [...this.state.attachments];

      // Mark custom form (if any) for deletion when changing to CEM4907
      if (this._selectedFormType === "cem4907" && attachments.some((a) => a.isCustomTa)) {
        attachments.forEach((a) => {
          if (a.isCustomTa) a.isDeleted = true;
        });
      }

      // Clear signatures (whether changing to CEM4907 or custom)
      const contractorsClient = _.cloneDeep(this.state.contractorsClient);
      for (const contractorId in contractorsClient) {
        contractorsClient[contractorId].contractorRepSignature = "";
        contractorsClient[contractorId].contractorRepSignedOn = null;
        contractorsClient[contractorId].contractorRepEmail = "";
        contractorsClient[contractorId].contractorRepName = "";
        contractorsClient[contractorId].contractorRepRemarks = "";
      }
      this.setState(
        {
          caltransRepSignature: "",
          caltransRepSignedOn: null,
          caltransRepName: "",
          caltransRepRemarks: "",
          contractorsClient,
          isLocked: false,
          selectedFormType: this._selectedFormType,
          isCustomTa: this._selectedFormType !== "cem4907",
          attachments,
        },
        () => !this.state.isChanged && this.markTaDirty()
      );
    }
  };

  handleAlert_deleteConfirm = (actionType) => (e) => {
    e.preventDefault();
    this.toggleAlert_deleteConfirm();
    if (actionType === "accept") {
      const { deleteTa, deleteAttachments, clearTempFilePaths, toggleTaModal } =
        this.props;
      // Delete all existing attachments
      let filePathsToDelete = [];
      this.state.attachments.forEach((a) => {
        filePathsToDelete = [...filePathsToDelete, a.filePath, a.imageThumbnailFilePath];
      });
      deleteAttachments(filePathsToDelete); // No need to await; fire and forget

      // Since above delete operation also deleted files uploaded during the current
      // modal session that were not yet saved into TaAttachments, we need to clear
      // redux store's adr.tempFilePaths
      clearTempFilePaths();

      deleteTa();
      toggleTaModal();
    }
  };

  handleAlert_cancelConfirm = (actionType) => (e) => {
    e.preventDefault();
    if (actionType === "accept") {
      this.toggleAlert_cancelConfirm();
      const {
        deleteTa,
        toggleTaModal,
        deleteTempAttachments,
        showTaModal,
        clearTempFilePaths,
      } = this.props;
      const { isNew } = this.state;

      // Delete all files uploaded during current modal session. (adr.tempFilePaths array
      // in redux store at this point will ONLY contain paths for files uploaded during
      // current modal session, because of the 'save wall' at the entry points of this modal
      // had already cleared tempFilePaths.)
      // Also, there's no need to separately clear adr.tempFilePaths in redux store since
      // the action clears it.
      deleteTempAttachments(); // No need to await; fire and forget

      clearTempFilePaths();

      toggleTaModal(!showTaModal);

      // If this TA was newly created, cancelling deletes the TA
      if (isNew) deleteTa();
    } else this.toggleAlert_cancelConfirm();
  };

  handleClick_delete = (e) => {
    e.preventDefault();
    this.toggleAlert_deleteConfirm();
  };

  email = async () => {
    const { adr, saveAdr } = this.props;
    const { isChanged } = this.state;
    if (isChanged) {
      this.save();
      await saveAdr(adr.assistantDailyReportId);
    }
    this.toggleModal_email();
  };

  handleClick_pdf = async (e) => {
    const { adr, saveAdr, getPdf } = this.props;
    const { tentativeAgreementId, isChanged } = this.state;
    e.preventDefault();
    if (isChanged) {
      this.save();
      await saveAdr(adr.assistantDailyReportId);
    }
    getPdf(tentativeAgreementId);
  };

  handleClick_cancel = (e) => {
    e.preventDefault();
    const { deleteTa } = this.props;
    const { isChanged, isNew } = this.state;
    if (isChanged) this.toggleAlert_cancelConfirm();
    else {
      if (isNew) deleteTa();
      this.props.toggleTaModal(false);
    }
  };

  handleClick_close = (e) => {
    e.preventDefault();
    this.props.toggleTaModal();
  };

  handleClick_save = (e) => {
    const { adr, toggleTaModal, showTaModal, saveAdr } = this.props;
    e.preventDefault();
    this.save();
    saveAdr(adr.assistantDailyReportId);
    toggleTaModal(!showTaModal);
  };

  changeJustificationModal_labor = (modalData) => {
    const {
      rowKey,
      changeAnnotation,
      showModal,
      toggleModal,
      cancelChange,
      updateCachedValues,
    } = modalData;
    const { user } = this.props;
    const { laborsClient } = this.state;

    const existCj = laborsClient[rowKey].changeJustification || "";
    const combinedCjAndPrefix = `${existCj ? `${existCj}; ` : ""}[${moment().format(
      "MM/DD/YYYY LT"
    )} by ${user.fullName}]: ${changeAnnotation}`;

    return this.changeJustificationModal({
      showModal,
      toggleModal,
      combinedChangeJustification: combinedCjAndPrefix,
      updateChangeJustification: this.applyChangeJustification_labor(rowKey),
      cancelChange,
      updateCachedValues,
    });
  };

  changeJustificationModal_equipment = (modalData) => {
    const {
      rowKey,
      changeAnnotation,
      showModal,
      toggleModal,
      cancelChange,
      updateCachedValues,
    } = modalData;
    const { user } = this.props;
    const { equipmentClient } = this.state;

    const existCj = equipmentClient[rowKey].changeJustification || "";
    const combinedCjAndPrefix = `${existCj ? `${existCj}; ` : ""}[${moment().format(
      "MM/DD/YYYY LT"
    )} by ${user.fullName}]: ${changeAnnotation}`;

    return this.changeJustificationModal({
      showModal,
      toggleModal,
      combinedChangeJustification: combinedCjAndPrefix,
      updateChangeJustification: this.applyChangeJustification_equipment(rowKey),
      cancelChange,
      updateCachedValues,
    });
  };

  changeJustificationModal_materials = (modalData) => {
    const {
      locationId,
      key,
      changeAnnotation,
      showModal,
      toggleModal,
      cancelChange,
      updateCachedValues,
    } = modalData;
    const { user } = this.props;
    const { materialsView } = this.state;

    const tam = (materialsView[locationId] || []).find((tam) => tam.key === key);
    if (!tam) return;
    const existCj = tam.changeJustification || "";
    const combinedCjAndPrefix = `${existCj ? `${existCj}; ` : ""}[${moment().format(
      "MM/DD/YYYY LT"
    )} by ${user.fullName}]: ${changeAnnotation}`;

    return this.changeJustificationModal({
      showModal,
      toggleModal,
      combinedChangeJustification: combinedCjAndPrefix,
      updateChangeJustification: this.applyChangeJustification_materials(locationId, key),
      cancelChange,
      updateCachedValues,
    });
  };

  changeJustificationModal = (props) => (
    <AdrTentativeAgreementChangeJustificationModalV3
      showModal={props.showModal}
      toggleModal={props.toggleModal}
      changeJustification={props.combinedChangeJustification}
      updateChangeJustification={props.updateChangeJustification}
      cancelChange={props.cancelChange}
      updateCachedValues={props.updateCachedValues}
    />
  );

  memoizedFileUploadContext = memoizeOne((jobStamp, adr, user, wpc, wpTabId) => ({
    fullEA: jobStamp.fullEA || "",
    targetCategory: 49,
    reportDate: (adr.reportStartDate && moment(adr.reportStartDate).format()) || null,
    userEmployeeNo: user.employeeNo,
    subContext: wpTabId in wpc ? wpc[wpTabId].wpShortCaption : "",
  }));

  memoizedTaUploadRequired = memoizeOne(
    (attachments, selectedFormType) =>
      selectedFormType === "custom" &&
      !attachments.some((a) => !a.isDeleted && a.isCustomTa)
  );

  renderTabContent = () => {
    const { tabId, projectId, status } = this.props;
    const {
      selectedTabId,
      laborsClient,
      isLocked,
      selectedLocations,
      equipmentClient,
      materialsView,
      contractorsClient,
    } = this.state;
    switch (selectedTabId) {
      case "labor":
        return (
          <div
            className="tab-pane fade show active border border-secondary"
            role="tabpanel"
          >
            <AdrTentativeAgreementModal_LaborSetV3
              tabId={tabId}
              projectId={projectId}
              status={status}
              isLocked={isLocked}
              laborsClient={laborsClient}
              updateLabor={this.updateLabor}
              changeJustificationModal={this.changeJustificationModal_labor}
              selectedContractors={(Object.keys(contractorsClient) || []).map((c) => +c)}
            />
          </div>
        );
      case "equipment":
        return (
          <div
            className="tab-pane fade show active border border-secondary"
            role="tabpanel"
          >
            <AdrTentativeAgreementModal_EquipmentSetV3
              tabId={tabId}
              projectId={projectId}
              status={status}
              isLocked={isLocked}
              equipmentClient={equipmentClient}
              updateEquipment={this.updateEquipment}
              changeJustificationModal={this.changeJustificationModal_equipment}
              selectedContractors={(Object.keys(contractorsClient) || []).map((c) => +c)}
            />
          </div>
        );
      case "materials":
        return (
          <div
            className="tab-pane fade show active border border-secondary"
            role="tabpanel"
          >
            <AdrTentativeAgreementModal_MaterialsV3
              tabId={tabId}
              projectId={projectId}
              status={status}
              isLocked={isLocked}
              selectedLocations={selectedLocations}
              materialsView={materialsView}
              updateMaterial={this.updateMaterial}
              changeJustificationModal={this.changeJustificationModal_materials}
            />
          </div>
        );
      default:
        return;
    }
  };

  renderLocationOptions = () => {
    const { selectedLocations, isLocked } = this.state;
    const locationSet = this.getLocationSet();
    return (
      <div className="d-flex flex-row flex-wrap ta-list">
        {locationSet.map((loc, index) => {
          const isSelected = loc.clientId in selectedLocations;
          return (
            <div key={`locOptionKey_${index}`} className="ta-list__item">
              <button
                key={`locId_${loc.clientId}`}
                id={`locId_${loc.clientId}`}
                name={`locId_${loc.clientId}`}
                className={`btn btn-sm ${
                  isSelected ? "btn-dark" : "btn-outline-secondary"
                }`}
                onClick={this.toggleLocation(loc.clientId)}
                onTouchEnd={this.toggleLocation(loc.clientId)}
                disabled={isLocked}
              >
                <FontAwesomeIcon
                  icon={isSelected ? ["far", "check-square"] : ["far", "square"]}
                  className="fa-fw mr-1"
                />
                {loc ? loc.locationName : "(Loc. Name N/A)"}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const {
      adr,
      clientId,
      tabId,
      jobStamp,
      user,
      projectId,
      showTaModal,
      status,
      workPerformedClient: wpc,
      addTempFilePaths,
    } = this.props;
    const {
      wpTabId,
      selectedTabId,
      showAlert_changeFormTypeConfirm,
      showAlert_cancelConfirm,
      showAlert_deleteConfirm,
      showAlert_lockConfirm,
      showModal_email,
      isLocked,
      isCustomTa,
      workDescription,
      contractorsClient,
      applyHoursToDR,
      laborsClient,
      equipmentClient,
      materialsView,
      attachments,
      selectedFormType,
    } = this.state;
    const closeBtn = (
      <button className="close" onClick={this.handleClick_cancel}>
        <FontAwesomeIcon icon="times" className="fa-fw text-white" />
      </button>
    );

    const fileUploadContext = this.memoizedFileUploadContext(
      jobStamp,
      adr,
      user,
      wpc,
      wpTabId
    );

    const taUploadRequired = this.memoizedTaUploadRequired(attachments, selectedFormType);

    return (
      <Modal isOpen={showTaModal} toggle={this.handleClick_cancel} size="lg2">
        <ModalHeader
          toggle={this.handleClick_cancel}
          className="bg-info text-white"
          close={closeBtn}
        >
          <FontAwesomeIcon icon="receipt" className="fa-fw mr-2" />
          {`Tentative Agreement For: ${
            adr ? new Date(adr.reportStartDate).toLocaleDateString() : "(Date N/A)"
          }, ${wpc[wpTabId] ? wpc[wpTabId].wpShortCaption : ""}`}
        </ModalHeader>
        <ModalBody>
          {isLocked && (
            <AlertBox color="info" icon="info-circle" className="mb-2">
              You are viewing a signed TA. You can still add/delete labor, equipment and
              materials and edit hours, but all other fields are locked from editing. You
              will be prompted to justify any changes.
            </AlertBox>
          )}
          <div className="form-row form-group mb-2">
            <label htmlFor="formType" className="col-sm-3 col-form-label my-auto">
              <b>Form Type:</b>
            </label>
            <div className="col-sm-9 my-auto">
              <div className="card">
                <div className="card-body m-2 p-0">
                  <div className="d-flex flex-row w-100">
                    <div className="form-check-inline custom-control custom-radio">
                      <div className="radio">
                        <input
                          type="radio"
                          name="taType_cem4907"
                          id="taType_cem4907"
                          checked={selectedFormType === "cem4907"}
                          onChange={this.handleChange_taType("cem4907")}
                          className="custom-control-input"
                          disabled={isLocked}
                        />
                        <label
                          htmlFor="taType_cem4907"
                          className="custom-control-label my-auto"
                        >
                          Standard TA (CEM-4907)
                        </label>
                      </div>
                    </div>
                    <div className="form-check-inline custom-control custom-radio ml-2">
                      <div className="radio">
                        <input
                          type="radio"
                          name="taType_custom"
                          id="taType_custom"
                          checked={selectedFormType === "custom"}
                          onChange={this.handleChange_taType("custom")}
                          className="custom-control-input"
                          disabled={isLocked}
                        />
                        <label
                          htmlFor="taType_custom"
                          className="custom-control-label my-auto"
                        >
                          Contractor-Provided (Custom)
                        </label>
                      </div>
                    </div>
                  </div>
                  {isCustomTa && (
                    <AdrTentativeAgreementCustomFormUploadV3
                      markTaDirty={this.markTaDirty}
                      addCustomFormAttachment={this.addCustomFormAttachment}
                      deleteCustomFormAttachment={this.deleteCustomFormAttachment}
                      customFormAttachments={(attachments || []).filter(
                        (a) => a.isCustomTa && !a.isDeleted
                      )}
                      fileUploadContext={fileUploadContext}
                      isLocked={isLocked}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="form-row form-group mb-2">
            <label htmlFor="contractors" className="col-sm-3 col-form-label my-auto">
              <b>Location(s):</b>
            </label>
            <div className="col-sm-9 my-auto">
              <div className="card">
                <div className="card-body m-2 p-0">{this.renderLocationOptions()}</div>
              </div>
            </div>
          </div>
          <div className="form-row form-group">
            <div className="col-sm-3">
              <label htmlFor="workDescription" className="col-form-label my-auto">
                <b>Work Description:</b>
              </label>
            </div>
            <div className="col-sm-9 my-auto">
              <input
                name="workDescription"
                id="workDescription"
                maxLength={80}
                className="form-control w-100"
                value={workDescription}
                onChange={this.handleChange}
                disabled={isLocked || status.isReadOnly}
              />
              <span className="text-small">
                {`${(workDescription || "").length}/80 characters`}
              </span>
            </div>
          </div>
          <AdrTentativeAgreementModalTabsV3
            tabs={this.tabs}
            selectedTabId={selectedTabId}
            selectTab={this.selectTab}
            laborsClient={laborsClient}
            equipmentClient={equipmentClient}
            materialsView={materialsView}
          />
          <div className="tab-content">{this.renderTabContent()}</div>
          <AdrTentativeAgreementSignaturesV3
            projectId={projectId}
            isLocked={isLocked}
            adrIsReadOnly={status.isReadOnly}
            formType={selectedFormType}
            caltransRepSignature={this.state.caltransRepSignature}
            isWetSignedByCaltransRep={this.state.isWetSignedByCaltransRep}
            caltransRepSignedOn={this.state.caltransRepSignedOn}
            caltransRepName={this.state.caltransRepName}
            caltransRepRemarks={this.state.caltransRepRemarks}
            isDisputedByCaltransRep={this.state.isDisputedByCaltransRep}
            applyCaltransSignature={this.applyCaltransSignature}
            contractorsClient={this.state.contractorsClient}
            applyContractorSignatures={this.applyContractorSignatures}
            email={this.email}
          />
          <AdrTentativeAgreementFileUploadV3
            attachments={attachments || []}
            add={this.addAttachments}
            update={this.updateAttachment}
            addTempFilePaths={addTempFilePaths}
            markTaDirty={this.markTaDirty}
            fileUploadContext={fileUploadContext}
            isLocked={isLocked}
            adrIsReadOnly={status.isReadOnly}
          />
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          {!status.isReadOnly ? (
            <div className="d-flex flex-row justify-content-between w-100">
              <div className="my-auto">
                <div className="custom-control-inline custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    name="applyHoursToDR"
                    id="applyHoursToDR"
                    className="custom-control-input my-auto"
                    value={applyHoursToDR}
                    checked={applyHoursToDR}
                    onChange={this.handleChange_check}
                    disabled={status.isReadOnly}
                  />
                  <label
                    htmlFor="applyHoursToDR"
                    className="custom-control-label my-auto"
                  >
                    Apply TA hours to the daily report (on save)
                  </label>
                </div>
              </div>
              <div>
                <button
                  id="taDelete"
                  name="taDelete"
                  className="btn btn-danger mr-2"
                  onClick={this.handleClick_delete}
                >
                  <FontAwesomeIcon icon="trash-alt" className="fa-fw mr-2" />
                  Delete TA
                </button>
                |
                <button
                  id="reviewTa"
                  name="reviewTa"
                  className="btn btn-secondary mx-2"
                  onClick={this.handleClick_pdf}
                >
                  <FontAwesomeIcon icon="file-pdf" className="fa-fw mr-2" />
                  View PDF
                </button>
                |
                <button
                  id="taCancel"
                  name="taCancel"
                  className="btn btn-secondary mx-2"
                  onClick={this.handleClick_cancel}
                >
                  Cancel
                </button>
                <button
                  id="taSave"
                  name="taSave"
                  className="btn btn-primary"
                  onClick={this.handleClick_save}
                >
                  <b>Save</b>
                </button>
              </div>
            </div>
          ) : (
            <button
              id="taClose"
              name="taClose"
              className="btn btn-secondary"
              onClick={this.handleClick_close}
            >
              Close
            </button>
          )}
        </ModalFooter>
        {showModal_email && (
          <AdrTentativeAgreementSendEmailModalV3
            showModal={showModal_email}
            toggleModal={this.toggleModal_email}
            tabId={tabId}
            taClientId={clientId}
            isLocked={isLocked}
            caltransEmail={
              user.employeeNo ? `${user.employeeNo.toLowerCase()}@dot.ca.gov` : ""
            }
            contractorEmail={
              contractorsClient[Object.keys(contractorsClient)[0]].contractorRepEmail ||
              ""
            }
          />
        )}
        {showAlert_changeFormTypeConfirm && (
          <AlertModal
            alertKey={"taChangeFormTypeConfirm"}
            alertTitle={"Confirm Form Type Change"}
            alertTitleClass="bg-warning"
            alertTitleIcon="exclamation-triangle"
            alertText={
              `This TA has one or more signatures, or you have uploaded a custom form but are attempting to change this TA's form type to a standard TA (CEM-4907).\n` +
              `Upon saving, any signature will be cleared and the custom form will be deleted.\n` +
              `Proceed?`
            }
            acceptCaption={"Yes"}
            acceptClass={"primary"}
            provideReject={true}
            rejectCaption="No"
            handleAlertAction={this.handleAlert_changeFormTypeConfirm}
            showAlertModal={showAlert_changeFormTypeConfirm}
            toggleAlertModal={this.toggleAlert_changeFormTypeConfirm}
          />
        )}
        {showAlert_lockConfirm && (
          <AlertModal
            alertKey={"taLockConfirm"}
            alertTitle={"TA Signed"}
            alertTitleClass="bg-info text-white"
            alertTitleIcon="info-circle"
            alertText={
              "At least one party has signed/disputed this TA." +
              "\nYou will be required to provide a written reason for all further changes."
            }
            acceptCaption={"OK"}
            acceptClass={"primary"}
            provideReject={false}
            handleAlertAction={this.toggleAlert_lockConfirm}
            showAlertModal={showAlert_lockConfirm}
            toggleAlertModal={this.toggleAlert_lockConfirm}
          />
        )}
        {showAlert_deleteConfirm && (
          <AlertModal
            alertKey={"taDeleteConfirm"}
            alertTitle={"Confirm Delete"}
            alertTitleClass="bg-warning"
            alertTitleIcon="exclamation-triangle"
            alertText="Are you sure you want to delete this TA?"
            acceptCaption={"Yes"}
            acceptClass={"danger"}
            provideReject={true}
            rejectCaption={"No"}
            rejectClass={"secondary"}
            handleAlertAction={this.handleAlert_deleteConfirm}
            showAlertModal={showAlert_deleteConfirm}
            toggleAlertModal={this.toggleAlert_deleteConfirm}
          />
        )}
        {showAlert_cancelConfirm && (
          <AlertModal
            alertKey={"taCancelConfirm"}
            alertTitle={"Confirm Cancel Changes"}
            alertTitleClass="bg-warning"
            alertTitleIcon="exclamation-triangle"
            alertText="There are unsaved changes. Are you sure you want to discard them and cancel?"
            acceptCaption={"Yes"}
            acceptClass={"danger"}
            provideReject={true}
            rejectCaption={"No"}
            rejectClass={"secondary"}
            handleAlertAction={this.handleAlert_cancelConfirm}
            showAlertModal={showAlert_cancelConfirm}
            toggleAlertModal={this.toggleAlert_cancelConfirm}
          />
        )}
      </Modal>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  save: (ta, applyHoursToDR) =>
    dispatch(adrActions.saveTentativeAgreement(ownProps.tabId, ta, applyHoursToDR)),
  deleteTa: () =>
    dispatch({
      type: ADR_EDIT.TENTATIVE_AGREEMENTS.DELETE,
      tabId: ownProps.tabId,
      clientId: ownProps.clientId,
    }),
  reviewPdf: (cem4907Data) => dispatch(cem4907PdfActions.request(cem4907Data)),
  getPdf: (taId) => dispatch(cem4907PdfActions.requestFromDb(taId)),
  addTempFilePaths: (tempFilePaths) =>
    dispatch({
      type: ADR_EDIT.TENTATIVE_AGREEMENTS.ADD_TEMP_FILEPATHS,
      tabId: ownProps.tabId,
      clientId: ownProps.clientId,
      filePaths: tempFilePaths,
    }),
  clearTempFilePaths: () =>
    dispatch({
      type: ADR_EDIT.TENTATIVE_AGREEMENTS.CLEAR_TEMP_FILEPATHS,
      tabId: ownProps.tabId,
      clientId: ownProps.clientId,
    }),
  deleteTempAttachments: () =>
    dispatch(
      adrActions.deleteTentativeAgreementTempFiles(ownProps.tabId, ownProps.clientId)
    ),
  deleteAttachments: (filePathsToDelete) =>
    dispatch(adrActions.deleteAttachments(filePathsToDelete)),
  saveAdr: async (adrId) => {
    dispatch(adrActions.clearTempFilePaths(ownProps.tabId));

    // Note ownProps.status is the adr's status (not TA's isNew)
    await dispatch(adrActions.save(ownProps.tabId, ownProps.status.isNew));
    await actionScripts.REFETCH_ADR_IN_TAB.run(ownProps.projectId, adrId, ownProps.tabId);
    // Saved, so mark as clean
    ownProps.markAdrClean();
  },
});

const mapStateToProps = (state, ownProps) => ({
  user: state.authentication.user,
  adr: state.adr.openAdrs[ownProps.tabId] || null,
  workPerformedClient: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].workPerformedClient || null
    : null,
  laborSetClient: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].laborSetClient || null
    : null,
  equipmentSetClient: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].equipmentSetClient || null
    : null,
  jobStamp: state.projects.jobStamps[ownProps.projectId] || null,
  projContractorList: state.contractors.projContractorLists[ownProps.projectId] || null,
  projLaborList: state.labors.projLaborLists[ownProps.projectId] || null,
  projEquipList: state.equipment.projEquipLists[ownProps.projectId] || null,
  projLocationList: state.locations.projLocationLists[ownProps.projectId] || null,
});

export default connect(mapStateToProps, mapDispatchToProps)(AdrTentativeAgreementModalV3);

function AdrTentativeAgreementUploadRequiredAlertV3() {
  return (
    <div className="d-flex flex-row w-100">
      <div className="alert alert-warning py-1 px-2 my-0 text-small">
        <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-2" />
        Upload scan(s) of this TA.
      </div>
    </div>
  );
}
